import { ACTIVE_DATASET, AUTHORING_MATCH_ID } from '../config'
import { cleanDraftText } from '../utils/text'
import { createAuthoringLayer, ensureAuthoringLayerState } from './layers'

export function createPromptProvenance() {
  return {
    selectionSummary: 'selection-summary-v1',
    storyInsight: 'story-insight-v1',
  }
}

/**
 * Builds the downloadable/importable annotation JSON without touching the DOM.
 */
export function createAnnotationExport(state, context = {}) {
  const normalized = ensureAuthoringLayerState(state)
  const usedModels = Array.from(
    new Set(
      [...normalized.highlightBands, ...normalized.storySnippets]
        .map(item => item.model)
        .filter(Boolean)
    )
  )
  const provenanceTimestamp = new Date().toISOString()
  const provenanceModel = context.currentStoryDraft
    ? context.currentStoryDraft.model || 'local-fallback'
    : context.lastSummaryModel || usedModels[0] || ''

  return {
    version: 2,
    datasetId: ACTIVE_DATASET.id,
    datasetLabel: ACTIVE_DATASET.label,
    matchId: AUTHORING_MATCH_ID,
    exportedAt: provenanceTimestamp,
    activeLayerId: normalized.activeLayerId,
    layers: normalized.layers,
    llmProvenance: {
      provider: 'external-or-local-fallback',
      model: provenanceModel,
      models: usedModels,
      promptVersions: createPromptProvenance(),
      timestamp: provenanceTimestamp,
      exportedAt: provenanceTimestamp,
    },
    highlightBands: normalized.highlightBands,
    storySnippets: normalized.storySnippets,
    auditLog: normalized.auditLog,
  }
}

/**
 * Normalizes imported annotation JSON while preserving the version 2 schema.
 */
export function normalizeImportedAuthoringAnnotations(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('Annotation file is not a JSON object.')
  }
  const importedBands = Array.isArray(data.highlightBands)
    ? data.highlightBands
    : []
  const importedSnippets = Array.isArray(data.storySnippets)
    ? data.storySnippets
    : []
  const importedAudit = Array.isArray(data.auditLog) ? data.auditLog : []
  let importedLayers = Array.isArray(data.layers) ? data.layers : []
  const now = new Date().toISOString()

  if (!importedLayers.length) {
    importedLayers = [
      createAuthoringLayer(
        data.matchId ? `${data.matchId} annotations` : 'Imported annotations',
        'layer_imported'
      ),
    ]
  }

  const seenLayerIds = new Set()
  const rawLayerIdMap = new Map()
  const normalizedLayers = importedLayers.map((layer, index) => {
    const rawId = cleanDraftText(layer.id, 80)
    let layerId = rawId || `layer_imported_${index + 1}`
    if (seenLayerIds.has(layerId)) {
      layerId = `${layerId}_${index + 1}`
    }
    seenLayerIds.add(layerId)
    if (rawId) rawLayerIdMap.set(rawId, layerId)
    return {
      id: layerId,
      name: cleanDraftText(layer.name, 80) || `Imported layer ${index + 1}`,
      visible: layer.visible !== false,
      createdAt: layer.createdAt || now,
      updatedAt: layer.updatedAt || now,
    }
  })

  const layerIds = new Set(normalizedLayers.map(layer => layer.id))
  const fallbackLayerId =
    rawLayerIdMap.get(data.activeLayerId) ||
    (layerIds.has(data.activeLayerId)
      ? data.activeLayerId
      : normalizedLayers[0].id)
  const normalizeLayerId = rawLayerId =>
    rawLayerIdMap.get(rawLayerId) ||
    (layerIds.has(rawLayerId) ? rawLayerId : fallbackLayerId)

  const normalizedBands = importedBands
    .filter(band => band && typeof band === 'object')
    .map((band, index) => ({
      ...band,
      id: cleanDraftText(band.id, 120) || `imported_band_${index + 1}`,
      layerId: normalizeLayerId(band.layerId),
    }))
  const normalizedBandById = new Map(
    normalizedBands.map(band => [band.id, band])
  )
  const normalizedSnippets = importedSnippets
    .filter(snippet => snippet && typeof snippet === 'object')
    .map((snippet, index) => {
      const band = normalizedBandById.get(snippet.bandId)
      return {
        ...snippet,
        id: cleanDraftText(snippet.id, 120) || `imported_snippet_${index + 1}`,
        layerId: normalizeLayerId(snippet.layerId || (band && band.layerId)),
      }
    })

  return {
    layers: normalizedLayers,
    activeLayerId: fallbackLayerId,
    highlightBands: normalizedBands,
    storySnippets: normalizedSnippets,
    auditLog: importedAudit,
    lastModel: data.llmProvenance ? data.llmProvenance.model || '' : '',
  }
}
